import { AnimatePresence, motion } from 'framer-motion';
import { FC, useId, useMemo } from 'react';

import { Box, useColorModeValue } from '@bonusly/design-system';

import { BoredSVG, STATE_PARTS } from './states';
import { BizyProps, BizySize, BizyState, isPartAnimatedState, PartAnimatedState } from './types';

/**
 * Bizy - The Bonusly bee mascot component
 *
 * A cute bee character that can display different emotional states.
 * Supports smooth part-based animations between states (antennae, wings, body, eyes, arms).
 * The bored state uses crossfade transition due to its unique rotated pose.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Bizy state="excited" />
 *
 * // With custom size
 * <Bizy state="thinking" size="lg" />
 *
 * // Numeric size
 * <Bizy state="curious" size={100} />
 * ```
 */
export const Bizy: FC<BizyProps> = ({ state = 'curious', size = 'md', freeze = false, className }) => {
	const sizeValue = useMemo(() => getSizeValue(size), [size]);

	return (
		<Box className={className} display="inline-flex" alignItems="center" justifyContent="center">
			<AnimatedBizySVG state={state} width={sizeValue} height={sizeValue} freeze={freeze} />
		</Box>
	);
};

const TRANSITION = {
	duration: 0.3,
	ease: [0.4, 0, 0.2, 1] as const,
};

const SPRING_TRANSITION = {
	type: 'spring' as const,
	stiffness: 200,
	damping: 20,
};

const WING_FLUTTER = {
	rotate: [0, -0.5, 0, 0.5, 0],
	scale: [1, 1.01, 1, 0.99, 1],
	transition: {
		duration: 0.05,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
	},
};

const WING_FLUTTER_OFFSET = {
	rotate: [0, 0.5, 0, -0.5, 0],
	scale: [1, 0.99, 1, 1.01, 1],
	transition: {
		duration: 0.05,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
		delay: 0.01,
	},
};

const getSizeValue = (size: BizySize): number => {
	if (typeof size === 'number') return size;
	const sizeMap: Record<string, number> = { sm: 48, md: 80, lg: 120 };
	return sizeMap[size] || 80;
};

const curvedStripePath = (y: number, h: number) =>
	`M70 ${y + 3}C120 ${y - 2} 180 ${y - 2} 240 ${y + 3}L240 ${y + h + 3}C180 ${y + h - 2} 120 ${y + h - 2} 70 ${y + h + 3}Z`;

interface AnimatedBizyProps {
	state: BizyState;
	width: number;
	height: number;
	freeze?: boolean;
	className?: string;
}

const FROZEN = { rotate: 0, scale: 1 };

const AnimatedBizySVG: FC<AnimatedBizyProps> = ({ state, width, height, freeze = false, className }) => {
	const instanceId = useId();
	const animatedState: PartAnimatedState = isPartAnimatedState(state) ? state : 'curious';
	const parts = useMemo(() => STATE_PARTS[animatedState], [animatedState]);
	const clipId = `clip_bizy_${instanceId}`;

	const leftArm = parts.arms.find((a) => a.side === 'left');
	const rightArm = parts.arms.find((a) => a.side === 'right');
	const isBored = state === 'bored';

	const outlineColor = useColorModeValue('#051236', '#515661');
	const outlineOpacity = useColorModeValue(1, 0.8);
	const wingOpacity = useColorModeValue(1, 0.8);
	const wingBlendMode = useColorModeValue('multiply', 'normal') as 'multiply' | 'normal';

	return (
		<AnimatePresence mode="wait">
			{!isBored ? (
				<motion.svg
					key="animated"
					className={className}
					width={width}
					height={height}
					viewBox="0 0 316 278"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.25 }}
				>
					{/* Left arm (behind body) */}
					{leftArm && (
						<motion.path
							d={leftArm.d}
							stroke={outlineColor}
							opacity={outlineOpacity}
							strokeWidth="18.2362"
							strokeLinecap="round"
							fill="none"
							initial={false}
							animate={{ d: leftArm.d }}
							transition={TRANSITION}
						/>
					)}

					{/* Wings */}
					<motion.g style={{ mixBlendMode: wingBlendMode, originX: '175px', originY: '120px' }} animate={freeze ? FROZEN : WING_FLUTTER}>
						<motion.path d={parts.wing1Path} fill="#DBDFE6" opacity={wingOpacity} initial={false} animate={{ d: parts.wing1Path }} transition={TRANSITION} />
					</motion.g>
					<motion.g style={{ mixBlendMode: wingBlendMode, originX: '175px', originY: '140px' }} animate={freeze ? FROZEN : WING_FLUTTER_OFFSET}>
						<motion.path d={parts.wing2Path} fill="#DBDFE6" opacity={wingOpacity} initial={false} animate={{ d: parts.wing2Path }} transition={TRANSITION} />
					</motion.g>
					<motion.g style={{ mixBlendMode: wingBlendMode, originX: '130px', originY: '120px' }} animate={freeze ? FROZEN : WING_FLUTTER}>
						<motion.path d={parts.wing3Path} fill="#DBDFE6" opacity={wingOpacity} initial={false} animate={{ d: parts.wing3Path }} transition={TRANSITION} />
					</motion.g>
					<motion.g style={{ mixBlendMode: wingBlendMode, originX: '130px', originY: '140px' }} animate={freeze ? FROZEN : WING_FLUTTER_OFFSET}>
						<motion.path d={parts.wing4Path} fill="#DBDFE6" opacity={wingOpacity} initial={false} animate={{ d: parts.wing4Path }} transition={TRANSITION} />
					</motion.g>

					{/* Antennae */}
					<motion.path fill={outlineColor} opacity={outlineOpacity} initial={false} animate={{ d: parts.antenna1.path }} transition={SPRING_TRANSITION} />
					<motion.path fill={outlineColor} opacity={outlineOpacity} initial={false} animate={{ d: parts.antenna2.path }} transition={SPRING_TRANSITION} />

					{/* Body with clip path */}
					<defs>
						<clipPath id={clipId}>
							<motion.rect
								fill="white"
								initial={false}
								animate={{ x: parts.body.x, y: parts.body.y, width: parts.body.width, height: parts.body.height, rx: parts.body.rx }}
								transition={SPRING_TRANSITION}
							/>
						</clipPath>
					</defs>

					<g clipPath={`url(#${clipId})`}>
						{/* Main body */}
						<motion.rect
							fill="#E6AE14"
							initial={false}
							animate={{ x: parts.body.x, y: parts.body.y, width: parts.body.width, height: parts.body.height, rx: parts.body.rx }}
							transition={SPRING_TRANSITION}
						/>

						{/* Body highlight */}
						<motion.rect
							fill="#FFCC55"
							initial={false}
							animate={{
								x: parts.bodyHighlight.x,
								y: parts.bodyHighlight.y,
								width: parts.bodyHighlight.width,
								height: parts.bodyHighlight.height,
								rx: parts.bodyHighlight.rx,
							}}
							transition={SPRING_TRANSITION}
						/>

						{/* Stripes (curved upward in center to match SVG design) */}
						<g style={{ mixBlendMode: 'darken' }}>
							<motion.path
								fill="#051236"
								initial={false}
								animate={{ d: curvedStripePath(parts.stripes.y1, parts.stripes.height) }}
								transition={SPRING_TRANSITION}
							/>
						</g>
						<g style={{ mixBlendMode: 'darken' }}>
							<motion.path
								fill="#051236"
								initial={false}
								animate={{ d: curvedStripePath(parts.stripes.y2, parts.stripes.height) }}
								transition={SPRING_TRANSITION}
							/>
						</g>
						<g style={{ mixBlendMode: 'darken' }}>
							<motion.path
								fill="#051236"
								initial={false}
								animate={{ d: curvedStripePath(parts.stripes.y3, parts.stripes.height) }}
								transition={SPRING_TRANSITION}
							/>
						</g>

						{/* Left arm inside clip (for thinking state chin-rest pose) */}
						{animatedState === 'thinking' && leftArm && (
							<motion.path
								d={leftArm.d}
								stroke={outlineColor}
								opacity={outlineOpacity}
								strokeWidth="18.2362"
								strokeLinecap="round"
								fill="none"
								initial={false}
								animate={{ d: leftArm.d }}
								transition={TRANSITION}
							/>
						)}
					</g>

					{/* Eyes (always dark, no color mode change) */}
					<motion.path fill="#051236" initial={false} animate={{ d: parts.eyes.leftPath }} transition={SPRING_TRANSITION} />
					<motion.path fill="#051236" initial={false} animate={{ d: parts.eyes.rightPath }} transition={SPRING_TRANSITION} />

					{/* Right antenna on top of body (for disappointed state droopy antenna) */}
					{animatedState === 'disappointed' && (
						<motion.path fill={outlineColor} opacity={outlineOpacity} initial={false} animate={{ d: parts.antenna2.path }} transition={SPRING_TRANSITION} />
					)}

					{/* Communicator Badge */}
					<g>
						<motion.ellipse
							fill="#4D3505"
							initial={false}
							animate={{ cx: parts.badge.outerEllipse.cx, cy: parts.badge.outerEllipse.cy, rx: parts.badge.outerEllipse.rx, ry: parts.badge.outerEllipse.ry }}
							transition={SPRING_TRANSITION}
						/>
						<motion.ellipse
							fill="#B37E00"
							initial={false}
							animate={{ cx: parts.badge.innerEllipse.cx, cy: parts.badge.innerEllipse.cy, rx: parts.badge.innerEllipse.rx, ry: parts.badge.innerEllipse.ry }}
							transition={SPRING_TRANSITION}
						/>
						<motion.path
							fill="#DBDFE6"
							fillRule="evenodd"
							clipRule="evenodd"
							initial={false}
							animate={{ d: parts.badge.starPath }}
							transition={SPRING_TRANSITION}
						/>
						<motion.path fill="#F6F6F9" initial={false} animate={{ d: parts.badge.starHighlightPath }} transition={SPRING_TRANSITION} />
					</g>

					{/* Stinger */}
					<motion.path fill={outlineColor} opacity={outlineOpacity} initial={false} animate={{ d: parts.stinger }} transition={TRANSITION} />

					{/* Right arm (in front of body) */}
					{rightArm && (
						<motion.path
							d={rightArm.d}
							stroke={outlineColor}
							opacity={outlineOpacity}
							strokeWidth="18.2362"
							strokeLinecap="round"
							fill="none"
							initial={false}
							animate={{ d: rightArm.d }}
							transition={TRANSITION}
						/>
					)}

					{/* Stars (for excited state) */}
					{!freeze &&
						parts.stars?.map((starPath, index) => (
							<motion.path
								key={`star-${index}`}
								d={starPath}
								fill="#FCC84F"
								initial={{ opacity: 0, scale: 0 }}
								animate={{
									opacity: [0, 1, 0.7, 1, 0.8, 1],
									scale: [0, 1.2, 0.9, 1.1, 0.95, 1],
									rotate: [0, 0, -5, 5, -3, 0],
								}}
								transition={{
									duration: 1.5,
									delay: index * 0.15,
									repeat: Infinity,
									repeatDelay: 0.5,
									ease: 'easeInOut',
								}}
								style={{ transformOrigin: 'center', transformBox: 'fill-box' }}
							/>
						))}
				</motion.svg>
			) : (
				<motion.div key="bored" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className={className}>
					<BoredSVG width={width} height={height} />
				</motion.div>
			)}
		</AnimatePresence>
	);
};
